import { useNotifications } from '../../providers/NotificationsProvider';
import { __, dummyArray } from '../../utils';
import NotificationsListItem from './NotificationsListItem';
import NotificationsListItemSkeleton from './NotificationsListItemSkeleton';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  InfiniteScrollLoader,
  List,
  Typography,
  type SxProps,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  isDesktop?: boolean;
  sx?: SxProps;
  onClose?: () => void;
  highlightIds?: string[];
};

const NotificationsList: React.FC<Props> = ({
  isDesktop,
  sx,
  onClose,
  highlightIds,
}) => {
  const { authUser } = useAuth();
  const {
    notifications,
    isLoading,
    isLoadingMore,
    hasMore,
    fetchInitial,
    fetchMore,
    markNotificationsAsReadMutation,
  } = useNotifications();

  const listRef = React.useRef<HTMLUListElement>(null);

  React.useEffect(() => {
    if (authUser) {
      fetchInitial();
    }
  }, [authUser, fetchInitial]);

  React.useEffect(() => {
    if (!notifications.length) return;
    const timer = setTimeout(() => {
      const unreadIds = notifications
        .filter((n) => !n.read_at)
        .map((n) => n.tag);
      if (unreadIds.length) {
        markNotificationsAsReadMutation.mutate(unreadIds);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [markNotificationsAsReadMutation, notifications, notifications.length]);

  return (
    <List
      ref={listRef}
      sx={{
        maxWidth: isDesktop ? 360 : 'unset',
        maxHeight: isDesktop ? 480 : 'unset',
        height: '100%',
        overflowY: 'auto',
        p: 0,
        ...sx,
      }}
    >
      {isLoading ? (
        dummyArray(7).map((_, index) => (
          <NotificationsListItemSkeleton key={index} />
        ))
      ) : notifications.length > 0 ? (
        notifications.map((notification, index) => (
          <NotificationsListItem
            notification={notification}
            onClose={onClose}
            key={index}
            highlight={highlightIds?.includes(notification.tag)}
          />
        ))
      ) : (
        <Typography p={3} align='center' variant='body2'>
          {__('Žiadne notifikácie')}
        </Typography>
      )}
      <InfiniteScrollLoader
        hasMore={hasMore}
        isLoading={isLoading || isLoadingMore}
        onVisible={fetchMore}
        targetRef={listRef as unknown as React.RefObject<HTMLDivElement>}
      />
    </List>
  );
};

export default NotificationsList;
